import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Nav } from "../components/Nav";
import { CheckCircle2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Cross-check the source of truth: the enrolment table.
          // Only show "Enrolled" if the backend actually granted access.
          try {
            const enr = await api.get(`/my/enrollments`);
            const active = (enr.data || []).some(
              (e) => e.status === "active" && e.days_remaining > 0
            );
            if (active) { setStatus("paid"); return; }
          } catch { /* fall through to retry */ }
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed"); return;
        }
      } catch { /* noop */ }
      if (attempts < 20) setTimeout(poll, 2500);
      else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-2xl mx-auto px-6 md:px-10 pt-24 text-center">
        {status === "polling" && (
          <>
            <p className="overline"><span className="gold-line mr-3" />Confirming payment</p>
            <h1 className="font-serif-display font-light text-5xl mt-6">One moment…</h1>
            <p className="mt-6 text-[#3a3a3a]">We're confirming your enrolment. This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-[#D4AF37] mx-auto" />
            <p className="overline mt-6"><span className="gold-line mr-3" />Enrolled</p>
            <h1 className="font-serif-display font-light text-5xl mt-6">Welcome to the studio.</h1>
            <p className="mt-6 text-[#3a3a3a]">A confirmation email is on its way. Your course is now unlocked.</p>
            <button data-testid="payment-goto-dashboard" onClick={() => nav("/dashboard")} className="btn-editorial mt-10">Enter Dashboard</button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle size={48} className="text-red-600 mx-auto" />
            <p className="overline mt-6"><span className="gold-line mr-3" />Something went wrong</p>
            <h1 className="font-serif-display font-light text-4xl mt-6">Payment not confirmed.</h1>
            <p className="mt-6 text-[#3a3a3a]">If your card was charged, please contact us and we'll sort it right away.</p>
            <Link to="/contact" className="btn-editorial mt-10">Contact Support</Link>
          </>
        )}
      </section>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-2xl mx-auto px-6 md:px-10 pt-24 text-center">
        <p className="overline"><span className="gold-line mr-3" />Payment Cancelled</p>
        <h1 className="font-serif-display font-light text-5xl mt-6">No worries.</h1>
        <p className="mt-6 text-[#3a3a3a]">Your card wasn't charged. You can restart the enrolment anytime.</p>
        <Link to="/course" data-testid="payment-cancel-retry" className="btn-editorial mt-10">Back to Course</Link>
      </section>
    </div>
  );
}
